/*
=========================================================
* Material Kit 2 React - v2.1.0
=========================================================
  
* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
  
Coded by www.creative-tim.com
  
 =========================================================
  
* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import { useState } from "react";
// @mui material components
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKInput from "components/MKInput";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";

function ContactUs() {
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    telefono: "",
    mensaje: "",
    website: "", // honeypot anti-spam
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Limpiar mensajes al editar
    if (error) setError("");
    if (success) setSuccess(false);
  };

  const MAX_NAME_LENGTH = 100;
  const MAX_EMAIL_LENGTH = 254;
  const MAX_PHONE_LENGTH = 30;
  const MAX_MESSAGE_LENGTH = 5000;

  const handleSubmit = async () => {
    if (!formData.nombre.trim()) {
      setError("Por favor, introduce tu nombre");
      return;
    }

    if (formData.nombre.length > MAX_NAME_LENGTH) {
      setError("El nombre no puede superar los 100 caracteres");
      return;
    }

    if (!formData.email.trim()) {
      setError("Por favor, introduce tu email");
      return;
    }

    if (formData.email.length > MAX_EMAIL_LENGTH) {
      setError("El email no puede superar los 254 caracteres");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Por favor, introduce un email válido");
      return;
    }

    if (formData.telefono && formData.telefono.length > MAX_PHONE_LENGTH) {
      setError("El teléfono no puede superar los 30 caracteres");
      return;
    }

    if (!formData.mensaje.trim()) {
      setError("Por favor, escribe tu mensaje");
      return;
    }

    if (formData.mensaje.length > MAX_MESSAGE_LENGTH) {
      setError("El mensaje no puede superar los 5000 caracteres");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch("/.netlify/functions/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Error al enviar el mensaje");
      }

      // Éxito
      setSuccess(true);
      setFormData({ nombre: "", email: "", telefono: "", mensaje: "", website: "" });

      // Enviar evento a Google Analytics si está disponible
      if (window.gtag) {
        window.gtag("event", "form_submit", {
          event_category: "Contact",
          event_label: "Contact Form Submitted",
        });
      }
    } catch (err) {
      console.error("Error:", err);
      setError(err.message || "Error al enviar el mensaje. Por favor, intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MKBox component="section" id="contacto">
      <MKBox pt={1} px={0}>
        <Container
          maxWidth={false}
          sx={{
            maxWidth: "1400px",
            mx: "auto",
          }}
        >
          <Grid container spacing={3} alignItems="stretch">
            {/* Columna izquierda: Mapa + Info rápida */}
            <Grid item xs={12} lg={6} sx={{ order: { xs: 2, lg: 1 } }}>
              <MKBox display="flex" flexDirection="column" gap={2} height="100%">
                {/* Por qué contactar con nosotros */}
                <MKBox bgColor="white" borderRadius="xl" shadow="lg" p={3}>
                  <MKTypography variant="h5" mb={2}>
                    ¿Por qué elegirnos?
                  </MKTypography>
                  {[
                    {
                      icon: "verified",
                      text: "Más de 60 años de experiencia y 2.000+ clientes satisfechos",
                    },
                    {
                      icon: "groups",
                      text: "Equipo de 20+ profesionales especializados en cada área",
                    },
                    {
                      icon: "support_agent",
                      text: "Atención personalizada y trato directo sin intermediarios",
                    },
                  ].map(({ icon, text }) => (
                    <MKBox key={icon} display="flex" alignItems="center" mb={1.5}>
                      <Icon color="info" sx={{ mr: 1.5, fontSize: "1.25rem" }}>
                        {icon}
                      </Icon>
                      <MKTypography variant="body2" color="text">
                        {text}
                      </MKTypography>
                    </MKBox>
                  ))}
                </MKBox>
                {/* Mapa */}
                <MKBox
                  bgColor="white"
                  borderRadius="xl"
                  shadow="lg"
                  sx={{ flex: 1, overflow: "hidden", minHeight: "400px" }}
                >
                  <iframe
                    title="Mapa Gestoría Moreno"
                    src="https://www.google.com/maps?q=Avenida+Divina+Pastora+9,+Granada&output=embed"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    sandbox="allow-scripts allow-same-origin allow-popups"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </MKBox>
              </MKBox>
            </Grid>

            <Grid item xs={12} lg={6} sx={{ order: { xs: 1, lg: 2 } }}>
              <MKBox
                bgColor="white"
                borderRadius="xl"
                shadow="lg"
                p={3}
                height="100%"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <MKBox
                  variant="gradient"
                  bgColor="info"
                  coloredShadow="info"
                  borderRadius="lg"
                  p={2}
                  mx={-1}
                  mt={-3}
                  mb={3}
                >
                  <MKTypography variant="h2" color="white">
                    Contacto
                  </MKTypography>
                  <MKTypography variant="body2" color="white" opacity={0.9} mt={0.5}>
                    Haznos tu consulta sin compromiso.
                  </MKTypography>
                </MKBox>

                <MKBox
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  {/* Horario */}
                  <MKBox display="flex" alignItems="flex-start" mb={2}>
                    <Icon color="info" sx={{ mr: 1.5, mt: 0.3, fontSize: "1.25rem" }}>
                      schedule
                    </Icon>
                    <MKBox>
                      <MKTypography variant="body2" color="text" fontWeight="bold">
                        Horario de atención
                      </MKTypography>
                      <MKTypography variant="body2" color="text">
                        Lunes a Jueves: 8:30 - 14:30 y 17:00 - 19:00 <br />
                        Viernes: 8:00 - 15:00
                      </MKTypography>
                    </MKBox>
                  </MKBox>

                  {/* Dirección */}
                  <MKBox display="flex" alignItems="flex-start" mb={2}>
                    <Icon color="info" sx={{ mr: 1.5, mt: 0.3, fontSize: "1.25rem" }}>
                      place
                    </Icon>
                    <MKTypography variant="body2" color="text">
                      Avenida Divina Pastora, 9, local 13, 18012 Granada
                    </MKTypography>
                  </MKBox>

                  {/* Teléfonos */}
                  <MKBox display="flex" alignItems="flex-start" mb={2}>
                    <Icon color="info" sx={{ mr: 1.5, mt: 0.3, fontSize: "1.25rem" }}>
                      phone
                    </Icon>
                    <MKBox>
                      <MKBox display="flex" flexWrap="wrap" gap={1} alignItems="center">
                        {[
                          { label: "958 805 110", href: "tel:+34958805110" },
                          { label: "958 805 111", href: "tel:+34958805111" },
                          { label: "958 805 113", href: "tel:+34958805113" },
                        ].map(({ label, href }) => (
                          <MKTypography
                            key={label}
                            component="a"
                            href={href}
                            variant="body2"
                            color="info"
                            fontWeight="medium"
                            sx={{
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {label}
                          </MKTypography>
                        ))}
                      </MKBox>
                      <MKBox display="flex" alignItems="center" mt={0.5}>
                        <MKTypography variant="caption" color="text" mr={0.5}>
                          FAX:
                        </MKTypography>
                        <MKTypography
                          component="a"
                          href="tel:+34958294021"
                          variant="caption"
                          color="info"
                          sx={{
                            textDecoration: "none",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          958 294 021
                        </MKTypography>
                      </MKBox>
                    </MKBox>
                  </MKBox>
                  <MKBox display="flex" alignItems="center" mb={2}>
                    <Icon color="info" sx={{ mr: 1.5, fontSize: "1.25rem" }}>
                      email
                    </Icon>
                    <MKBox>
                      <MKTypography
                        component="a"
                        variant="body2"
                        color="info"
                        fontWeight="bold"
                        href="mailto:info@gestoriamoreno.com"
                      >
                        info@gestoriamoreno.com
                      </MKTypography>
                    </MKBox>
                  </MKBox>

                  <Divider sx={{ my: 1 }} />

                  {/* Formulario */}
                  <MKTypography variant="body2" color="text" fontWeight="bold" mb={1.5}>
                    Envíanos un mensaje
                  </MKTypography>

                  <MKBox component="form">
                    {/* Honeypot anti-spam */}
                    <input
                      type="text"
                      name="website"
                      value={formData.website}
                      onChange={(e) => handleInputChange("website", e.target.value)}
                      autoComplete="off"
                      tabIndex={-1}
                      style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0 }}
                      aria-hidden="true"
                    />
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <MKBox mb={0}>
                          <MKInput
                            label="Nombre completo"
                            fullWidth
                            value={formData.nombre}
                            onChange={(e) => handleInputChange("nombre", e.target.value)}
                            inputProps={{ maxLength: 100 }}
                          />
                        </MKBox>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MKBox mb={0}>
                          <MKInput
                            type="email"
                            label="Email"
                            fullWidth
                            value={formData.email}
                            onChange={(e) => handleInputChange("email", e.target.value)}
                            inputProps={{ maxLength: 254 }}
                          />
                        </MKBox>
                      </Grid>
                      <Grid item xs={12}>
                        <MKBox mb={0}>
                          <MKInput
                            type="tel"
                            label="Teléfono (opcional)"
                            fullWidth
                            value={formData.telefono}
                            onChange={(e) => handleInputChange("telefono", e.target.value)}
                            inputProps={{ maxLength: 30 }}
                          />
                        </MKBox>
                      </Grid>
                      <Grid item xs={12}>
                        <MKBox>
                          <MKInput
                            label="¿En qué podemos ayudarte?"
                            multiline
                            rows={3}
                            fullWidth
                            value={formData.mensaje}
                            onChange={(e) => handleInputChange("mensaje", e.target.value)}
                            inputProps={{ maxLength: 5000 }}
                          />
                        </MKBox>
                      </Grid>

                      {/* Mensaje de éxito */}
                      {success && (
                        <Grid item xs={12}>
                          <MKBox
                            bgColor="success"
                            color="white"
                            p={2}
                            borderRadius="lg"
                            textAlign="center"
                          >
                            <MKTypography variant="body2" color="white">
                              ✓ ¡Mensaje enviado correctamente! Te responderemos lo antes posible.
                            </MKTypography>
                          </MKBox>
                        </Grid>
                      )}

                      {/* Mensaje de error */}
                      {error && (
                        <Grid item xs={12}>
                          <MKBox
                            bgColor="error"
                            color="white"
                            p={2}
                            borderRadius="lg"
                            textAlign="center"
                          >
                            <MKTypography variant="body2" color="white">
                              ✗ {error}
                            </MKTypography>
                          </MKBox>
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <MKBox mt={2}>
                          <MKButton
                            variant="gradient"
                            color="info"
                            fullWidth
                            onClick={handleSubmit}
                            disabled={loading}
                          >
                            {loading ? "Enviando..." : "Enviar Mensaje"}
                          </MKButton>
                        </MKBox>
                      </Grid>
                    </Grid>
                  </MKBox>
                </MKBox>
              </MKBox>
            </Grid>
          </Grid>
        </Container>
      </MKBox>
    </MKBox>
  );
}
export default ContactUs;
